import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  IconButton,
  Chip,
  Paper,
  Typography,
  TablePagination,
  CircularProgress,
  Modal,
  Alert,
  Snackbar,
} from "@mui/material";

import {
  Download,
  Visibility,
  Close,
} from "@mui/icons-material";
import moment from "moment";
import { getVasOrders } from "../auth/service";
import "./VasPurchase.css";

// ============================================
// Status Configuration
// ============================================

const STATUS_CONFIG = {
  CONFIRMED: {
    label: "Confirmed",
    color: "#2e7d32",
  },
  PENDING: {
    label: "Pending",
    color: "#ff9800",
  },
  APPROVED: {
    label: "Approved",
    color: "#1976d2",
  },
  REJECTED: {
    label: "Rejected",
    color: "#f44336",
  },
  COMPLETED: {
    label: "Completed",
    color: "#4caf50",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "#d32f2f",
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "#1976d2",
  },
  PROCESSED: {
    label: "Processed",
    color: "#4caf50",
  },
};

// ============================================
// Error Boundary Component
// ============================================

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="vas-booknow-wrapper">
          <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              We're sorry, but an error occurred while loading this page.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Paper>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================
// Main Component
// ============================================

const VasPurchase = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [componentError, setComponentError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    search: "",
  });

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ============================================
  // Helper Functions
  // ============================================

  const getPaymentMethod = (booking) => {
    try {
      return booking?.transactionDetails?.paymentMethod || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getUpiVpa = (booking) => {
    try {
      return booking?.transactionDetails?.upiVpa || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getRrn = (booking) => {
    try {
      return booking?.transactionDetails?.rrn || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getTransactionId = (booking) => {
    try {
      return booking?.transactionId || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getObenCoinsUsed = (booking) => {
    try {
      return booking?.transactionDetails?.obenCoinsUsed || 0;
    } catch {
      return 0;
    }
  };

  const getCustomerName = (booking) => {
    try {
      return booking?.customerDetails?.name || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getCustomerPhone = (booking) => {
    try {
      return booking?.customerDetails?.phone || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getCustomerEmail = (booking) => {
    try {
      return booking?.customerDetails?.email || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getServiceName = (booking) => {
    try {
      const otherProducts = booking?.transactionDetails?.otherProductDetails;
      if (otherProducts && Array.isArray(otherProducts) && otherProducts.length > 0) {
        return otherProducts.map(p => p.Name).join(", ");
      }
      return booking?.productDetails?.productName || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getPolicyNumber = (booking) => {
    try {
      const otherProducts = booking?.transactionDetails?.otherProductDetails;
      if (otherProducts && Array.isArray(otherProducts) && otherProducts.length > 0) {
        return otherProducts.map(p => p.Code).join(", ");
      }
      return booking?.zohoRecordId || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getProvider = (booking) => {
    try {
      return "Oben Electric";
    } catch {
      return "N/A";
    }
  };

  const getAmount = (booking) => {
    try {
      return booking?.amount || 0;
    } catch {
      return 0;
    }
  };

  const getStatus = (booking) => {
    try {
      return booking?.status || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getCreatedAt = (booking) => {
    try {
      return booking?.createdAt || null;
    } catch {
      return null;
    }
  };

  const getUpdatedAt = (booking) => {
    try {
      return booking?.updatedAt || null;
    } catch {
      return null;
    }
  };

  const getZohoRecordId = (booking) => {
    try {
      return booking?.zohoRecordId || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getMerchantOrderReference = (booking) => {
    try {
      return booking?.merchantOrderReference || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getPineLabOrderId = (booking) => {
    try {
      return booking?.pineLabOrderId || "N/A";
    } catch {
      return "N/A";
    }
  };

  const getFinalPrice = (booking) => {
    try {
      return booking?.productDetails?.finalPrice || 0;
    } catch {
      return 0;
    }
  };

  const getObenCoinsAmount = (booking) => {
    try {
      return booking?.transactionDetails?.pricingDetails?.obenCoinsAmount || 0;
    } catch {
      return 0;
    }
  };

  const getRedeemedCoins = (booking) => {
    try {
      return booking?.transactionDetails?.pricingDetails?.redeemedCoins || 0;
    } catch {
      return 0;
    }
  };

  const getTransactionAmountValue = (booking) => {
    try {
      return booking?.transactionDetails?.transactionAmountValue || 0;
    } catch {
      return 0;
    }
  };

  const getPricingFinalPrice = (booking) => {
    try {
      return booking?.transactionDetails?.pricingDetails?.finalPrice || 0;
    } catch {
      return 0;
    }
  };

  const getErrorMessage = (error) => {
    if (!error) return "Something went wrong";
    if (typeof error === "string") return error;

    if (error.message && typeof error.message === "string") {
      if (error.message === "{}" || error.message === "[object Object]") {
        return "Something went wrong";
      }
      return error.message;
    }

    if (error.response?.data?.message) {
      const msg = error.response.data.message;
      if (typeof msg === "string") {
        if (msg === "{}" || msg === "[object Object]") {
          return "Something went wrong";
        }
        return msg;
      }
      if (typeof msg === "object") {
        if (msg.message) return msg.message;
        if (msg.code) {
          return `Operation failed: ${msg.code}`;
        }
      }
    }

    if (error.error && typeof error.error === "object") {
      if (error.error.message) {
        return error.error.message;
      }
    }

    return "Something went wrong";
  };

  const safeString = (value, defaultValue = "N/A") => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === "object") return defaultValue;
    return String(value);
  };

  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const safeGet = (obj, path, defaultValue = null) => {
    try {
      const keys = path.split(".");
      let result = obj;
      for (const key of keys) {
        if (result === null || result === undefined) return defaultValue;
        result = result[key];
      }
      return result !== undefined && result !== null ? result : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const formatCurrency = (amount) => {
    try {
      const num = safeNumber(amount);
      return `₹${num.toLocaleString("en-IN")}`;
    } catch {
      return "₹0";
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return moment(dateString).format("DD-MMM-YYYY HH:mm");
    } catch {
      return "Invalid Date";
    }
  };

  // ============================================
  // API Functions
  // ============================================

  const fetchBookings = async () => {
    setLoading(true);
    setComponentError(null);
    try {
      const params = {
        page: page + 1,
        pageSize: rowsPerPage,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim();
      }

      const res = await getVasOrders(params);

      if (res && res.user) {
        const userData = res.user;

        if (userData.data && Array.isArray(userData.data)) {
          setBookings(userData.data);
          setTotalCount(safeNumber(userData.totalCount, 0));
          setTotalAmount(safeNumber(userData.totalAmount, 0));
          setTotalPages(safeNumber(userData.totalPages, 1));
        } else {
          setBookings([]);
          setTotalCount(0);
          setTotalAmount(0);
          setTotalPages(1);
          showNotification("No bookings found", "info");
        }
      } else if (res && res.data && Array.isArray(res.data)) {
        setBookings(res.data);
        setTotalCount(safeNumber(res.totalCount || res.data.length, 0));
        setTotalAmount(safeNumber(res.totalAmount, 0));
        setTotalPages(safeNumber(res.totalPages, 1));
      } else if (res && Array.isArray(res)) {
        setBookings(res);
        setTotalCount(res.length);
        setTotalAmount(res.reduce((sum, item) => sum + (item.amount || 0), 0));
        setTotalPages(1);
      } else {
        console.warn("Invalid response structure for VAS orders:", res);
        setBookings([]);
        setTotalCount(0);
        setTotalAmount(0);
        setTotalPages(1);

        if (!res) {
          showNotification("No response from server", "error");
        } else if (res.error) {
          showNotification(res.message || "Error fetching data", "error");
        } else {
          showNotification("No bookings found", "info");
        }
      }
    } catch (error) {
      console.error("Error fetching VAS bookings:", error);
      setBookings([]);
      setTotalCount(0);
      setTotalPages(1);
      setComponentError(error);

      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Event Handlers
  // ============================================

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage, filters.search]);

  const handleSearchChange = (event) => {
    try {
      setFilters({ ...filters, search: event.target.value });
      setPage(0);
    } catch (error) {
      console.error("Error in search:", error);
      showNotification("Error applying search", "error");
    }
  };

  const handleChangePage = (event, newPage) => {
    try {
      setPage(newPage);
    } catch (error) {
      console.error("Error changing page:", error);
      showNotification("Error changing page", "error");
    }
  };

  const handleChangeRowsPerPage = (event) => {
    try {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    } catch (error) {
      console.error("Error changing rows per page:", error);
      showNotification("Error changing rows per page", "error");
    }
  };

  const handleViewBooking = (booking) => {
    try {
      if (!booking || typeof booking !== "object") {
        showNotification("Invalid data", "error");
        return;
      }
      setSelectedBooking(booking);
      setShowBookingDetails(true);
    } catch (error) {
      console.error("Error viewing details:", error);
      showNotification("Error viewing details", "error");
    }
  };

  const showNotification = (message, severity = "success") => {
    try {
      let displayMessage = "Something went wrong";

      if (typeof message === "string") {
        if (message && message !== "{}" && message !== "[object Object]") {
          displayMessage = message;
        }
      } else if (message && typeof message === "object") {
        const extractedMessage = getErrorMessage(message);
        if (
          extractedMessage &&
          extractedMessage !== "{}" &&
          extractedMessage !== "[object Object]"
        ) {
          displayMessage = extractedMessage;
        }
      }

      setSnackbar({
        open: true,
        message: displayMessage,
        severity: severity,
      });
    } catch (error) {
      console.error("Error showing notification:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  // ============================================
  // Export Function
  // ============================================

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const params = {
        page: 1,
        pageSize: 10000,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim();
      }

      const res = await getVasOrders(params);

      let exportData = [];

      if (res && res.user && res.user.data && Array.isArray(res.user.data)) {
        exportData = res.user.data;
      } else if (res && res.data && Array.isArray(res.data)) {
        exportData = res.data;
      } else if (res && Array.isArray(res)) {
        exportData = res;
      } else {
        exportData = bookings;
      }

      if (exportData.length === 0) {
        showNotification("No data to export", "warning");
        setExportLoading(false);
        return;
      }

      const formattedData = exportData.map((booking) => ({
        "VAS Booking ID": booking._id,
        "Transaction ID": getTransactionId(booking),
        "Customer Name": getCustomerName(booking),
        "Customer Phone": getCustomerPhone(booking),
        "Customer Email": getCustomerEmail(booking),
        "Service Name": getServiceName(booking),
        "Policy Number": getPolicyNumber(booking),
        "Provider": getProvider(booking),
        "Amount": formatCurrency(getAmount(booking)),
        "Oben Coins Used": getObenCoinsUsed(booking),
        "Status": safeString(getStatus(booking)),
        "Payment Method": getPaymentMethod(booking),
        "Payment Status": safeGet(booking, "transactionDetails.paymentStatus", "N/A"),
        "UPI VPA": getUpiVpa(booking),
        "RRN": getRrn(booking),
        "Booking Date": formatDate(getCreatedAt(booking)),
        "Zoho Record ID": getZohoRecordId(booking),
        "Merchant Order Ref": getMerchantOrderReference(booking),
        "PineLab Order ID": getPineLabOrderId(booking),
      }));

      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "VAS_Bookings");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const { default: saveAs } = await import("file-saver");
      saveAs(blob, `VAS_Bookings_${moment().format("DD-MMM-YYYY_HHmm")}.xlsx`);

      showNotification("Export completed successfully", "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setExportLoading(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  if (componentError) {
    return (
      <div className="vas-booknow-wrapper">
        <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {getErrorMessage(componentError)}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => {
                setComponentError(null);
                fetchBookings();
              }}
            >
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Box>
        </Paper>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="vas-booknow-wrapper">
        <div className="vas-booknow-header-container">
          <span className="vas-booknow-header">VAS Purchase</span>
          <div className="vas-btn-container">
            {exportLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                onClick={handleExportData}
                variant="contained"
                className="vas-bordered-button"
                startIcon={<Download />}
                disabled={bookings.length === 0}
              >
                Export Data
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="vas-stats-container">
          <Paper elevation={0} className="vas-stat-card">
            <Typography variant="h6" color="textSecondary">
              Total Records
            </Typography>
            <Typography variant="h4">{totalCount}</Typography>
          </Paper>
          <Paper elevation={0} className="vas-stat-card">
            <Typography variant="h6" color="textSecondary">
              Total Amount
            </Typography>
            <Typography variant="h4">{formatCurrency(totalAmount)}</Typography>
          </Paper>
        </div>

        {/* Filters - Only Search with decreased width */}
        <div className="vas-filters-container">
          <TextField
            label="Search by customer name, phone number"
            variant="outlined"
            value={filters.search}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300, maxWidth: 500 }}
          />
        </div>

        {/* Data Table */}
        <div className="vas-table-container">
          {loading ? (
            <div className="vas-loading-container">
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading data...
              </Typography>
            </div>
          ) : (
            <>
              <table className="vas-bookings-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Phone</th>
                    <th>Transaction ID</th>
                    <th>Service Name</th>
                    <th>Amount</th>
                    <th>Oben Coins Used</th>
                    <th>Status</th>
                    <th>Payment Method</th>
                    <th>Booking Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="vas-no-data">
                        <Typography variant="body1">No data found</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Try adjusting your search
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    bookings.map((item) => {
                      try {
                        if (!item || typeof item !== "object") return null;

                        const status = getStatus(item);
                        let statusConfig = STATUS_CONFIG[status];
                        if (!statusConfig) {
                          statusConfig = {
                            label: status || "Unknown",
                            color: "#757575",
                          };
                        }

                        return (
                          <tr key={item._id}>
                            <td>{getCustomerName(item)}</td>
                            <td>{getCustomerPhone(item)}</td>
                            <td>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "12px",
                                  fontFamily: "monospace",
                                }}
                              >
                                {getTransactionId(item)}
                              </Typography>
                            </td>
                            <td>{getServiceName(item)}</td>
                            <td>{formatCurrency(getAmount(item))}</td>
                            <td>{getObenCoinsUsed(item)}</td>
                            <td>
                              <Chip
                                label={statusConfig.label}
                                size="small"
                                sx={{
                                  backgroundColor: statusConfig.color,
                                  color: "#fff",
                                }}
                              />
                            </td>
                            <td>{getPaymentMethod(item)}</td>
                            <td>{formatDate(getCreatedAt(item))}</td>
                            <td>
                              <IconButton
                                onClick={() => handleViewBooking(item)}
                                color="primary"
                                size="small"
                                title="View Details"
                              >
                                <Visibility />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      } catch (error) {
                        console.error("Error rendering row:", error);
                        return null;
                      }
                    })
                  )}
                </tbody>
              </table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className="vas-pagination"
                showFirstButton
                showLastButton
              />
            </>
          )}
        </div>

        {/* Details Modal */}
        <Modal
          open={showBookingDetails}
          onClose={() => setShowBookingDetails(false)}
          aria-labelledby="details-modal"
        >
          <Paper className="vas-booking-details-modal">
            <div className="vas-modal-header">
              <Typography variant="h5">VAS Purchase Details</Typography>
              <IconButton onClick={() => setShowBookingDetails(false)}>
                <Close />
              </IconButton>
            </div>

            {selectedBooking && (
              <div className="vas-modal-content">
                {/* Customer Information */}
                <div className="vas-details-section">
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <div className="vas-details-grid">
                    <div>
                      <strong>Name:</strong> {getCustomerName(selectedBooking)}
                    </div>
                    <div>
                      <strong>Phone:</strong> {getCustomerPhone(selectedBooking)}
                    </div>
                    <div>
                      <strong>Email:</strong> {getCustomerEmail(selectedBooking)}
                    </div>
                    <div>
                      <strong>Country Code:</strong> {safeGet(selectedBooking, "customerDetails.countryCode", "N/A")}
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="vas-details-section">
                  <Typography variant="h6" gutterBottom>
                    Service Details
                  </Typography>
                  <div className="vas-details-grid">
                    <div>
                      <strong>Service Name:</strong> {getServiceName(selectedBooking)}
                    </div>
                    <div>
                      <strong>Policy Number:</strong> {getPolicyNumber(selectedBooking)}
                    </div>
                    <div>
                      <strong>Provider:</strong> {getProvider(selectedBooking)}
                    </div>
                    <div>
                      <strong>Zoho Record ID:</strong> {getZohoRecordId(selectedBooking)}
                    </div>
                    <div>
                      <strong>Zoho Status:</strong> {safeGet(selectedBooking, "zohoStatus", "N/A")}
                    </div>
                  </div>
                </div>

                {/* Booking Information */}
                <div className="vas-details-section">
                  <Typography variant="h6" gutterBottom>
                    Booking Information
                  </Typography>
                  <div className="vas-details-grid">
                    <div>
                      <strong>Booking ID:</strong> {selectedBooking._id}
                    </div>
                    <div>
                      <strong>Transaction ID:</strong> {getTransactionId(selectedBooking)}
                    </div>
                    <div>
                      <strong>Merchant Order Reference:</strong> {getMerchantOrderReference(selectedBooking)}
                    </div>
                    <div>
                      <strong>PineLab Order ID:</strong> {getPineLabOrderId(selectedBooking)}
                    </div>
                    <div>
                      <strong>Amount:</strong> {formatCurrency(getAmount(selectedBooking))}
                    </div>
                    <div>
                      <strong>Oben Coins Used:</strong> {getObenCoinsUsed(selectedBooking)}
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <Chip
                        label={STATUS_CONFIG[getStatus(selectedBooking)]?.label || getStatus(selectedBooking)}
                        size="small"
                        sx={{
                          backgroundColor: STATUS_CONFIG[getStatus(selectedBooking)]?.color || "#1976d2",
                          color: "#fff",
                          ml: 1,
                        }}
                      />
                    </div>
                    <div>
                      <strong>Booking Date:</strong> {formatDate(getCreatedAt(selectedBooking))}
                    </div>
                    <div>
                      <strong>Last Updated:</strong> {formatDate(getUpdatedAt(selectedBooking))}
                    </div>
                  </div>
                </div>

                {/* Price Details Section */}
                <div className="vas-details-section">
                  <Typography variant="h6" gutterBottom>
                    Price Details
                  </Typography>
                  <div className="vas-details-grid">
                    <div>
                      <strong>Final Price (Product):</strong> {formatCurrency(getFinalPrice(selectedBooking))}
                    </div>
                    <div>
                      <strong>Transaction Amount:</strong> {formatCurrency(getTransactionAmountValue(selectedBooking))}
                    </div>
                    <div>
                      <strong>Pricing Final Price:</strong> {formatCurrency(getPricingFinalPrice(selectedBooking))}
                    </div>
                    <div>
                      <strong>Oben Coins Amount:</strong> {formatCurrency(getObenCoinsAmount(selectedBooking))}
                    </div>
                    <div>
                      <strong>Redeemed Coins:</strong> {getRedeemedCoins(selectedBooking)}
                    </div>
                    <div>
                      <strong>Oben Coins Used:</strong> {getObenCoinsUsed(selectedBooking)}
                    </div>
                    <div>
                      <strong>Currency:</strong> {safeGet(selectedBooking, "currency", "INR")}
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                {selectedBooking.productDetails && (
                  <div className="vas-details-section">
                    <Typography variant="h6" gutterBottom>
                      Product Details
                    </Typography>
                    <div className="vas-details-grid">
                      <div>
                        <strong>Product Name:</strong> {safeGet(selectedBooking, "productDetails.productName", "N/A")}
                      </div>
                      <div>
                        <strong>Product Code:</strong> {safeGet(selectedBooking, "productDetails.productCode", "N/A")}
                      </div>
                      <div>
                        <strong>VIN:</strong> {safeGet(selectedBooking, "productDetails.vin", "N/A")}
                      </div>
                      <div>
                        <strong>KMS Ridden:</strong> {safeGet(selectedBooking, "productDetails.kmsRidden", "N/A")}
                      </div>
                      <div>
                        <strong>Final Price:</strong> {formatCurrency(safeGet(selectedBooking, "productDetails.finalPrice", 0))}
                      </div>
                      <div>
                        <strong>Description:</strong> {safeGet(selectedBooking, "productDetails.description", "N/A")}
                      </div>
                      <div>
                        <strong>UTM URL:</strong>
                        <a
                          href={safeGet(selectedBooking, "productDetails.utmUrl", "#")}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "none", marginLeft: "8px" }}
                        >
                          {safeGet(selectedBooking, "productDetails.utmUrl", "N/A")}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction Details */}
                {selectedBooking.transactionDetails && (
                  <div className="vas-details-section">
                    <Typography variant="h6" gutterBottom>
                      Transaction Details
                    </Typography>
                    <div className="vas-details-grid">
                      <div>
                        <strong>Payment Method:</strong> {getPaymentMethod(selectedBooking)}
                      </div>
                      <div>
                        <strong>Payment Status:</strong> {safeGet(selectedBooking, "transactionDetails.paymentStatus", "N/A")}
                      </div>
                      <div>
                        <strong>Transaction Status:</strong> {safeGet(selectedBooking, "transactionDetails.status", "N/A")}
                      </div>
                      <div>
                        <strong>Transaction Date:</strong> {formatDate(safeGet(selectedBooking, "transactionDetails.transactionCreatedAt"))}
                      </div>
                      <div>
                        <strong>Payment Success At:</strong> {formatDate(safeGet(selectedBooking, "transactionDetails.paymentSuccessAt"))}
                      </div>
                      <div>
                        <strong>Oben Coins Used:</strong> {getObenCoinsUsed(selectedBooking)}
                      </div>
                      <div>
                        <strong>Transaction Amount Value:</strong> {formatCurrency(getTransactionAmountValue(selectedBooking))}
                      </div>
                      {getUpiVpa(selectedBooking) !== "N/A" && (
                        <div>
                          <strong>UPI VPA:</strong> {getUpiVpa(selectedBooking)}
                        </div>
                      )}
                      {getRrn(selectedBooking) !== "N/A" && (
                        <div>
                          <strong>RRN:</strong> {getRrn(selectedBooking)}
                        </div>
                      )}
                      {safeGet(selectedBooking, "transactionDetails.arn") && (
                        <div>
                          <strong>ARN:</strong> {safeGet(selectedBooking, "transactionDetails.arn", "N/A")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Other Product Details */}
                {selectedBooking.transactionDetails?.otherProductDetails &&
                  Array.isArray(selectedBooking.transactionDetails.otherProductDetails) &&
                  selectedBooking.transactionDetails.otherProductDetails.length > 0 && (
                    <div className="vas-details-section">
                      <Typography variant="h6" gutterBottom>
                        VAS Products
                      </Typography>
                      {selectedBooking.transactionDetails.otherProductDetails.map((product, index) => (
                        <div key={index} className="vas-details-grid" style={{ marginBottom: '12px', padding: '12px', background: '#f9f9f9', borderRadius: '8px' }}>
                          <div>
                            <strong>Name:</strong> {product.Name || "N/A"}
                          </div>
                          <div>
                            <strong>Code:</strong> {product.Code || "N/A"}
                          </div>
                          <div>
                            <strong>MRP:</strong> {formatCurrency(product.Total_Cost_to_Customer_MRP || 0)}
                          </div>
                          <div>
                            <strong>Selling Price:</strong> {formatCurrency(product.Selling_Price_New || 0)}
                          </div>
                          <div>
                            <strong>IGST:</strong> {product.IGST || 0}%
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Memo URL Section */}
                {selectedBooking.memoUrl && (
                  <div className="vas-details-section">
                    <Typography variant="h6" gutterBottom>
                      Additional Documents
                    </Typography>
                    <div className="vas-details-grid">
                      <div>
                        <strong>Memo URL:</strong>
                        <a
                          href={selectedBooking.memoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "none", marginLeft: "8px" }}
                        >
                          Download Memo
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Modal>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </ErrorBoundary>
  );
};

export default VasPurchase;